import { useEffect, useState } from 'react';
import { hasPermission } from '@firsttable/functions';
import {
  RESTAURANT_STATUS_ORDER,
  restaurantStatusLabels,
  type RestaurantStatus,
} from '@discover-data/stores/restaurant/model/restaurantStatuses/options';
import usePage from '@discover-data/stores/hooks/page/usePage';
import { useStoreActions, useStoreState } from '@discover-data/stores/restaurant/hooks';
import { cn } from '@discover-lib/utils';
import CheckboxGroup from '@discover-ui/checkboxGroup';

const STAFF_VENUE_STATUS_PERMISSIONS = ['ADMIN', 'BOOK_NON_LIVE_VENUES'] as const;

/**
 * Permission reads localStorage; false until mounted so SSR and first client paint match (avoids hydration mismatches).
 */
export function useCanManageRestaurantStatusFilter(): boolean {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) {
    return false;
  }
  return hasPermission([...STAFF_VENUE_STATUS_PERMISSIONS]);
}

export type RestaurantStatusesFilterProps = React.ComponentPropsWithoutRef<'div'>;

export default function RestaurantStatusesFilter({
  className,
  ...props
}: RestaurantStatusesFilterProps) {
  const value = useStoreState((state) => state.restaurantStatuses.value);
  const set = useStoreActions((actions) => actions.restaurantStatuses.set);
  const { resetPage } = usePage();
  const canShow = useCanManageRestaurantStatusFilter();

  if (!canShow) {
    return null;
  }

  const options = RESTAURANT_STATUS_ORDER.map((status) => ({
    value: status,
    label: restaurantStatusLabels[status],
  }));

  return (
    <div className={cn('flex flex-col gap-2', className)} {...props}>
      <div className="flex items-center justify-between">
        <h3 className="font-bold">Venue status</h3>
      </div>
      <CheckboxGroup
        rowClassName="py-0"
        labelClassName="py-0 leading-snug"
        options={options}
        values={value}
        setValues={(newValues) => {
          resetPage();
          const allowed = newValues.filter((v): v is RestaurantStatus =>
            (RESTAURANT_STATUS_ORDER as readonly string[]).includes(v)
          );
          set(allowed);
        }}
      />
    </div>
  );
}
